// src/components/PieChart.js
import { useEffect, useRef } from "react";
import { Chart as Chartjs, registerables } from 'chart.js';
import { Line, Chart } from "react-chartjs-2";
import { getRelativePosition } from 'chart.js/helpers';
import PropTypes from "prop-types";
import moment from "moment";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";
import data from "components/Maps/HeatMap/data";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;
var scroll_event_happening = false;

function GradientReader(field, fields, height, pixel) {

  const canvas = document.createElement('canvas');         // create canvas element
  const ctx = canvas.getContext('2d');                     // get context
  var gradientBg = ctx.createLinearGradient(0, 0, 0, height);    // create a gradient

  canvas.width = 1;                                        // 101 pixels incl.
  canvas.height = height;                                  // as the gradient

  if (field == null) {
    gradientBg.addColorStop(0.85, "rgba(38, 124, 204, 0.85)"); //0.2
    gradientBg.addColorStop(0.70, "rgba(131, 180, 226, 0.85)"); //0.7
    gradientBg.addColorStop(0.4, "rgba(239,128,98,0.85)"); //1
    gradientBg.addColorStop(0.1, "rgba(255, 24, 24, 0.85)"); //1
  }
  else {
    field.gradient(gradientBg, fields);
  }

  ctx.fillStyle = gradientBg;                                     // set as fill style
  ctx.fillRect(0, 0, canvas.width, canvas.height);        // draw a single line

  // method to get color of gradient at % position [0, 100]
  //console.log(pixel)
  return ctx.getImageData(0, pixel, 1, 1).data;
}



function GradientMinMaxLineChart({ chartData = [], field = null, advancedToggle, min, suggestedMin, min_value, max, suggestedMax, max_value, startTime, endTime, createdAt, setStartTime, setEndTime }) {

  const chartRef = useRef(null);
  var fields = field.server_name.split(",");
  var used_fields = [...new Set(Object.entries(min_value).filter(([k, v]) => v != null).map(([k, v]) => k).concat(Object.entries(max_value).filter(([k, v]) => v != null).map(([k, v]) => k)))];

  if (!advancedToggle) {
    var temp = used_fields[0];
    used_fields = [temp];
  }

  const scrollEvent = (chart) => {
    // console.log(timeout);
    current_chart = chart;
    if (typeof timeout != 'undefined') {
      clearTimeout(timeout);
      timeout = undefined;
    }
    scroll_event_happening = true;

    timeout = setTimeout(() => {
      runWheelScrollEvent(current_chart);
      scroll_event_happening = false;
      timeout = undefined;
    }, 200);
  }

  const runWheelScrollEvent = ({ chart }) => {
    var start_time, end_time, group_size;
    let min_limit = moment(0)
    let max_limit = moment();
    // console.log(chart.scales.x.min)
    let min_moment = moment(chart.scales.x.min);
    let max_moment = moment(chart.scales.x.max);

    if (min_moment.isBefore(min_limit)) {
      start_time = min_limit;
    }
    else {
      start_time = min_moment;
    }

    if (max_moment.isAfter(max_limit)) {
      end_time = max_limit;
    }
    else {
      end_time = max_moment;
    }

    setStartTime(start_time);
    setEndTime(end_time);

  };

  var config = {
    plugins: []
  };

  var options = {
    layout: {
      padding: 8
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0
    },
    plugins: {
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: true,
          },
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          speed: 0.5, sensitivity: 0.5,
          onZoomComplete: scrollEvent,
        },
        limits: {
          x: {
            min: moment(createdAt).unix() * 1000,
            max: moment().unix() * 1000,
            minRange: 16000 * 1000,
          }
        },
        legend: {
          position: 'top',
        },
        title: {
          display: false,
          text: field.label
        },
        tooltip: {
          enabled: true,
        }
      },
      legend: {
        labels: {
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            const {
              labels: {
                usePointStyle,
                pointStyle,
                textAlign,
                color
              }
            } = chart.legend.options;

            return chart._getSortedDatasetMetas().map((meta) => {
              const style = meta.controller.getStyle(usePointStyle ? 0 : undefined);

              try {
                const { chartArea: { top, bottom, height }, scales: { x, y } } = chart;

                for (var field_nm of used_fields) {
                  if (datasets[meta.index].label == "Average") {
                    var labelFill = "black";
                  }
                  else {
                    if (datasets[meta.index].label == "Upper Range") {
                      if (used_fields.length > 1) {
                        var pixel = chart.scales[datasets[meta.index].yAxisID].getPixelForValue(max_value[field_nm])
                      }
                      else {
                        var pixel = y.getPixelForValue(max_value[field_nm])
                      }
                    }
                    else {
                      if (used_fields.length > 1) {
                        var pixel = chart.scales[datasets[meta.index].yAxisID].getPixelForValue(min_value[field_nm])
                      }
                      else {
                        var pixel = y.getPixelForValue(min_value[field_nm])
                      }
                    }

                    var col = GradientReader(field, used_fields, Math.ceil(height), Math.floor(pixel - top));

                    if (!col[0] && !col[1] && !col[2] && !col[3]) {
                      var labelFill = "black";
                    }
                    else {
                      var labelFill = `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${col[3] / 255})`;
                    }
                  }
                }
              }
              catch (e) {
                // console.log(e);
                var labelFill = "black";
              }

              return {
                text: datasets[meta.index].label,
                fillStyle: labelFill,
                fontColor: color,
                hidden: !meta.visible,
                lineCap: style.borderCapStyle,
                lineDash: style.borderDash,
                lineDashOffset: style.borderDashOffset,
                lineJoin: style.borderJoinStyle,
                lineWidth: 1,
                strokeStyle: labelFill,
                pointStyle: pointStyle || style.pointStyle,
                rotation: style.rotation,
                textAlign: textAlign || style.textAlign,
                borderRadius: 0,
                datasetIndex: meta.index
              };
            }, this);
          }
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 5
      }
    }
  };

  const customBorder = {
    id: 'customBorder',
    afterDatasetsDraw(chart, args, pluginOptions) {
      const { ctx, chartArea: { top, bottom, left, right, width, height } } = chart;
      var used_fields = [...new Set(Object.entries(min_value).filter(([k, v]) => v != null).map(([k, v]) => k).concat(Object.entries(max_value).filter(([k, v]) => v != null).map(([k, v]) => k)))];

      if (used_fields.length > 1) {
        used_fields.forEach((field, index) => {
          const createLine = (counter) => {
            var line_start_y = top + (((bottom - top) / used_fields.length) * counter);

            ctx.save();
            ctx.beginPath();
            ctx.lineWidth = 1;
            ctx.strokeStyle = 'rgb(105, 105, 105)';
            ctx.moveTo(left, line_start_y);
            ctx.lineTo(right, line_start_y);
            ctx.closePath();
            ctx.stroke();
          }

          createLine(index);
          if (index + 1 == used_fields.length) createLine(index + 1);
        });
      }
      else {
        ctx.save();
      }
    }
  };

  var y_scale_bounds = {
    suggestedMin: suggestedMin[field.field],
    suggestedMax: suggestedMax[field.field],
  };

  if (min) {
    y_scale_bounds.min = min[field.field];
  }
  if (max) {
    y_scale_bounds.max = max[field.field];
  }

  if (used_fields.length == 1) {
    options.maintainAspectRatio = false;
    config.plugins = false;

    options.scales = {
      x: {
        stacked: true,
        type: 'time',
        min: startTime,
        max: endTime
      },
      y: { ...y_scale_bounds, display: true },
      y1: { display: false },
      y2: { display: false },
      y3: { display: false },
      y4: { display: false }
    };
  }
  else if (used_fields.length > 1) {
    options.plugins.legend.display = false;
    config.plugins.push(customBorder);

    options.scales = {
      x: {
        stacked: true,
        type: 'time',
        min: startTime,
        max: endTime
      },
      y: { display: false }
    };

    for (var field_nm of used_fields) {
      var y_scale_bounds = {
        suggestedMin: suggestedMin[field_nm],
        suggestedMax: suggestedMax[field_nm],
      };

      if (min) {
        y_scale_bounds.min = min[field_nm];
      }
      if (max) {
        y_scale_bounds.max = max[field_nm];
      }

      options.scales[`y${used_fields.indexOf(field_nm) + 1}`] = { ...y_scale_bounds };
      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].display = true;
      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].stackWeight = 1;
      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].stack = 'rssiStack';
      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].offset = true;

      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].afterTickToLabelConversion = (ctx) => {
        ctx.ticks = [{ value: ctx.min, label: ctx.min }, { value: ((ctx.max / 2) + (ctx.min / 2)), label: ((ctx.max / 2) + (ctx.min / 2)) }, { value: ctx.max, label: ctx.max }];
      }

      options.scales[`y${used_fields.indexOf(field_nm) + 1}`].title = {
        display: true,
        text: field_nm.toUpperCase()
      }
    }
  }

  useEffect(() => {
    const chart = chartRef.current;
    if (chart) {
      chart.stop();
      chart.update('none');
    }
  }, [chartData, advancedToggle]);

  const activePlugins = used_fields.length > 1 ? [customBorder] : [];
  var chart = (
    <Line
      key={`${fields.join(',')}-${advancedToggle}-${used_fields.length}`}
      ref={chartRef}
      data={chartData}
      options={options}
      plugins={activePlugins}
    />
  );

  return chart;
};

// Typechecking props for the GradientMinMaxLineChart
GradientMinMaxLineChart.propTypes = {
  chartData: PropTypes.object,
  min: PropTypes.number,
  suggestedMin: PropTypes.number,
  min_value: PropTypes.number,
  max: PropTypes.number,
  suggestedMax: PropTypes.number,
  max_value: PropTypes.number,
  field: PropTypes.object,
  advancedToggle: PropTypes.bool,
  startTime: PropTypes.instanceOf(moment()),
  setStartTime: PropTypes.func,
  endTime: PropTypes.instanceOf(moment()),
  setEndTime: PropTypes.func,
  createdAt: PropTypes.string,
};


export default GradientMinMaxLineChart;